
import { LayoutDashboard, Receipt, FileBox, MessageCircle, BarChart } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../Button";

const PracticeManagementContent = () => {
  return (
    <div className="container mx-auto px-4 py-16">
      {/* Hero Section */}
      <div className="mb-16 text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-6">Law Practice Management</h1>
        <p className="text-xl md:text-2xl text-white/70 max-w-3xl mx-auto">
          AI-powered practice management tools enhance efficiency and productivity for legal teams.
        </p>
      </div>

      {/* Overview Section */}
      <div className="mb-20">
        <div className="prose prose-lg prose-invert max-w-4xl mx-auto">
          <p>
            AI-powered law practice management tools offer comprehensive features that enhance efficiency and productivity for legal teams. Benefits include increased productivity, reduced operational costs, improved accuracy in workflows, better client satisfaction, and enhanced profitability. These tools empower legal teams to focus on strategic tasks while maintaining organized and efficient operations.
          </p>
        </div>
      </div>

      {/* Key Features Section */}
      <div className="mb-20">
        <h2 className="text-3xl font-bold mb-12 text-center">Key Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-blue-400">
              <LayoutDashboard size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Workflow Automation</h3>
            <p className="text-white/70">
              Automates routine tasks like case tracking, document creation, and deadline management, reducing administrative burdens and saving time.
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-purple-400">
              <Receipt size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Billing and Timekeeping</h3>
            <p className="text-white/70">
              Tracks billable hours automatically and generates accurate invoices, improving transparency and profitability.
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-green-400">
              <FileBox size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Document Management</h3>
            <p className="text-white/70">
              Organizes legal files with tagging, profiling, and full-text search functionality, enabling quick access to contracts, case files, and correspondence.
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-amber-400">
              <MessageCircle size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Client Relationship Management</h3>
            <p className="text-white/70">
              Streamlines client communication and engagement by automating updates and providing actionable insights into client needs.
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-red-400">
              <BarChart size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Analytics and Reporting</h3>
            <p className="text-white/70">
              Provides data-driven insights into firm operations, helping optimize resource allocation and performance.
            </p>
          </div>
        </div>
      </div>

      {/* Case Studies Section */}
      <div className="mb-20">
        <h2 className="text-3xl font-bold mb-12 text-center">Curated Case Studies</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Intake Efficiency</h3>
            <p className="text-white/70 mb-4">
              How Pritzker Hageman used Clio to reduce intake admin-task time by 500%
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://www.clio.com/customers/how-pritzker-hageman-used-clio-to-reduce-intake-admin-task-time-by-500/', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Future of Practice</h3>
            <p className="text-white/70 mb-4">
              Pioneering the Future of Legal Practice and Client Service with Harvey AI
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://www.harvey.ai/customers/macfarlanes', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Case Volume Management</h3>
            <p className="text-white/70 mb-4">
              How PracticePanther Helps Eckart Wostmann Wiese Efficiently Manage 5,000+ Criminal Defense Cases a Year
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://www.practicepanther.com/customer-story/eckart-wostmann-wiese-efficiently-manages-5000-criminal-defense-cases-a-year-with-practicepanther/', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Streamlined Operations</h3>
            <p className="text-white/70 mb-4">
              Streamlining Operations: How Stebelton Snider Enhanced Efficiency with Coyote Analytics
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://surepoint.com/resources/blog/case-study-streamlining-workflows-and-increasing-efficiency/', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Family Law Efficiency</h3>
            <p className="text-white/70 mb-4">
              Family Law Firm Doubles Efficiency Using MyCase
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://www.mycase.com/case-studies/easterling-family-law/', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Estate Planning</h3>
            <p className="text-white/70 mb-4">
              An estate planning firm saves time and drives profitability with Smokeball
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://www.smokeball.com/case-studies/sawyer-sawyer', '_blank')}
            >
              Read Study
            </Button>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="text-center">
        <h2 className="text-3xl font-bold mb-6">Elevate Your Practice Management</h2>
        <p className="text-xl text-white/70 mb-8 max-w-2xl mx-auto">
          Discover how our AI-powered practice management solutions can help your legal team operate more efficiently.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/contact">
            <Button variant="glass" size="lg" hasAnimation={true}>Contact Us</Button>
          </Link>
          <Link to="/services/custom-solutions">
            <Button variant="outline" size="lg">Explore Custom Solutions</Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PracticeManagementContent;
